// Three.js Wind Effect for High Mountain Region Feel
class WindEffect {
  constructor() {
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.particles = null;
    this.windParticles = [];
    this.animationId = null;
    this.isInitialized = false;
    
    // Wind parameters
    this.windStrength = 0.05;
    this.particleCount = 50;
    this.windDirection = new THREE.Vector3(1, 0, 0);
    this.turbulence = 0.3;
    
    this.init();
  }

  init() {
    // Check if Three.js is loaded
    if (typeof THREE === 'undefined') {
      console.warn('Three.js not loaded. Wind effect disabled.');
      return;
    }

    this.createScene();
    this.createCamera();
    this.createRenderer();
    this.createWindParticles();
    this.animate();
    this.handleResize();
    this.isInitialized = true;
  }

  createScene() {
    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.Fog(0x87CEEB, 50, 200); // Mountain fog effect
  }

  createCamera() {
    this.camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    this.camera.position.z = 100;
  }

  createRenderer() {
    this.renderer = new THREE.WebGLRenderer({ 
      alpha: true, 
      antialias: true 
    });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setClearColor(0x000000, 0);
    
    // Add to body
    this.renderer.domElement.style.position = 'fixed';
    this.renderer.domElement.style.top = '0';
    this.renderer.domElement.style.left = '0';
    this.renderer.domElement.style.width = '100%';
    this.renderer.domElement.style.height = '100%';
    this.renderer.domElement.style.pointerEvents = 'none';
    this.renderer.domElement.style.zIndex = '1';
    
    document.body.appendChild(this.renderer.domElement);
  }

  createWindParticles() {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(this.particleCount * 3);
    const velocities = new Float32Array(this.particleCount * 3);
    const sizes = new Float32Array(this.particleCount);
    const opacities = new Float32Array(this.particleCount);

    for (let i = 0; i < this.particleCount; i++) {
      const i3 = i * 3;
      
      // Random starting positions
      positions[i3] = (Math.random() - 0.5) * 200;
      positions[i3 + 1] = (Math.random() - 0.5) * 200;
      positions[i3 + 2] = (Math.random() - 0.5) * 200;
      
      // Random velocities
      velocities[i3] = (Math.random() - 0.5) * 0.5;
      velocities[i3 + 1] = (Math.random() - 0.5) * 0.5;
      velocities[i3 + 2] = (Math.random() - 0.5) * 0.5;
      
      // Random sizes
      sizes[i] = Math.random() * 2 + 0.5;
      
      // Random opacities
      opacities[i] = Math.random() * 0.8 + 0.2;
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('velocity', new THREE.BufferAttribute(velocities, 3));
    geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
    geometry.setAttribute('opacity', new THREE.BufferAttribute(opacities, 1));

    // Create particle material
    const material = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        windStrength: { value: this.windStrength },
        windDirection: { value: this.windDirection },
        turbulence: { value: this.turbulence }
      },
      vertexShader: `
        attribute float size;
        attribute float opacity;
        attribute vec3 velocity;
        
        uniform float time;
        uniform float windStrength;
        uniform vec3 windDirection;
        uniform float turbulence;
        
        varying float vOpacity;
        varying vec3 vColor;
        
        void main() {
          vOpacity = opacity;
          
          // Mountain wind colors (white, light blue, gray)
          float colorMix = sin(time * 0.5 + position.x * 0.01) * 0.5 + 0.5;
          vColor = mix(vec3(0.9, 0.95, 1.0), vec3(0.7, 0.8, 0.9), colorMix);
          
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = size * (300.0 / -mvPosition.z);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        varying float vOpacity;
        varying vec3 vColor;
        
        void main() {
          // Create circular particle
          float distance = length(gl_PointCoord - vec2(0.5));
          if (distance > 0.5) discard;
          
          // Soft edges
          float alpha = (1.0 - distance * 2.0) * vOpacity;
          
          gl_FragColor = vec4(vColor, alpha);
        }
      `,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });

    this.particles = new THREE.Points(geometry, material);
    this.scene.add(this.particles);
  }

  animate() {
    this.animationId = requestAnimationFrame(() => this.animate());
    
    if (!this.isInitialized) return;

    const time = Date.now() * 0.001;
    
    // Update wind direction with subtle changes
    this.windDirection.x = Math.sin(time * 0.1) * 0.5 + 1;
    this.windDirection.y = Math.sin(time * 0.15) * 0.3;
    this.windDirection.z = Math.sin(time * 0.08) * 0.2;
    
    // Update material uniforms
    this.particles.material.uniforms.time.value = time;
    this.particles.material.uniforms.windDirection.value = this.windDirection;
    this.particles.material.uniforms.windStrength.value = this.windStrength;
    
    // Update particle positions
    const positions = this.particles.geometry.attributes.position.array;
    const velocities = this.particles.geometry.attributes.velocity.array;
    
    for (let i = 0; i < this.particleCount; i++) {
      const i3 = i * 3;
      
      // Apply wind force
      velocities[i3] += this.windDirection.x * this.windStrength * 0.01;
      velocities[i3 + 1] += this.windDirection.y * this.windStrength * 0.01;
      velocities[i3 + 2] += this.windDirection.z * this.windStrength * 0.01;
      
      // Add turbulence
      velocities[i3] += (Math.random() - 0.5) * this.turbulence * 0.01;
      velocities[i3 + 1] += (Math.random() - 0.5) * this.turbulence * 0.01;
      velocities[i3 + 2] += (Math.random() - 0.5) * this.turbulence * 0.01;
      
      // Update positions
      positions[i3] += velocities[i3];
      positions[i3 + 1] += velocities[i3 + 1];
      positions[i3 + 2] += velocities[i3 + 2];
      
      // Reset particles that go too far
      if (positions[i3] > 100) positions[i3] = -100;
      if (positions[i3] < -100) positions[i3] = 100;
      if (positions[i3 + 1] > 100) positions[i3 + 1] = -100;
      if (positions[i3 + 1] < -100) positions[i3 + 1] = 100;
      if (positions[i3 + 2] > 100) positions[i3 + 2] = -100;
      if (positions[i3 + 2] < -100) positions[i3 + 2] = 100;
    }
    
    this.particles.geometry.attributes.position.needsUpdate = true;
    this.particles.rotation.y += 0.001;
    
    this.renderer.render(this.scene, this.camera);
  }

  handleResize() {
    window.addEventListener('resize', () => {
      if (!this.isInitialized) return;
      
      this.camera.aspect = window.innerWidth / window.innerHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(window.innerWidth, window.innerHeight);
    });
  }

  // Public methods to control wind
  setWindStrength(strength) {
    this.windStrength = Math.max(0, Math.min(2, strength));
  }

  setParticleCount(count) {
    this.particleCount = Math.max(50, Math.min(500, count));
    this.scene.remove(this.particles);
    this.createWindParticles();
  }

  destroy() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    if (this.renderer && this.renderer.domElement) {
      document.body.removeChild(this.renderer.domElement);
    }
    this.isInitialized = false;
  }
}

// Initialize wind effect when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  // Load Three.js if not already loaded
  if (typeof THREE === 'undefined') {
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
    script.onload = () => {
      window.windEffect = new WindEffect();
    };
    document.head.appendChild(script);
  } else {
    window.windEffect = new WindEffect();
  }
});

// Export for global access
window.WindEffect = WindEffect;
